// Cửa vào trang Gom Đơn (/inbox: tin nhắn + bình luận + kết nối kênh, kiểu Pancake). Nằm trong gói chính, còn trang thật tải lười — đơn vị
// chưa mở Gom Đơn không tải phần này (kèm socket.io-client). Thứ tự: RequireRole (main.tsx) → hỏi /gomdon/api/me →
// chưa mở thì báo, mở rồi mới tải trang. Trang Gom Đơn lỗi (kể cả tải mã trang lỗi sau khi deploy) chỉ hỏng phần nội
// dung, khung menu của ShopTrack vẫn dùng được.

import { Component, lazy, Suspense, type ReactNode } from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { MessageCircleOff, RotateCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadError } from '@/components/ui/data-notice'
import { Spinner } from '@/components/ui/spinner'
import { useGomdonAccess, type GomdonAccess } from './access'
import { legacyGomdonTarget } from './paths'

const InboxPage = lazy(() => import('./pages/Inbox'))

function Loading() {
  return (
    <div className="flex items-center justify-center gap-2 py-16 text-sm text-muted">
      <Spinner /> Đang tải…
    </div>
  )
}

/** Shop chưa mở Gom Đơn / không có quyền / engine không trả lời. Nhận trạng thái từ GomdonPage (không gắn thêm query). */
function Unavailable({ access }: { access: GomdonAccess }) {
  const { error, fetching, refetch } = access
  // 403: shop ngoài danh sách được mở, hoặc nhân viên thiếu quyền (engine nói rõ lý do)
  if (!error || error.status === 403) {
    const text = !error || error.code === 'not_enabled' ? 'Gom Đơn chưa mở cho shop này' : error.message
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
        <span className="grid size-10 place-items-center rounded-full bg-paperSoft text-muted">
          <MessageCircleOff className="size-5" aria-hidden />
        </span>
        <p className="text-sm text-inkSoft">{text}</p>
      </div>
    )
  }
  // Lỗi có mã của engine (vd 503 unavailable) đã nói rõ lý do; còn lại (mạng, engine tắt, proxy 5xx) thì nói chung
  return (
    <LoadError onRetry={fetching ? undefined : refetch}>
      {fetching ? 'Đang kết nối Gom Đơn…' : error.code ? error.message : 'Chưa kết nối được Gom Đơn, thử lại sau.'}
    </LoadError>
  )
}

class PageErrorBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  override state = { failed: false }
  static getDerivedStateFromError() {
    return { failed: true }
  }
  override componentDidCatch(err: unknown) {
    console.error('[gomdon] trang lỗi', err)
  }
  override render() {
    if (!this.state.failed) return this.props.children
    return (
      <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center" role="alert">
        <p className="text-sm text-inkSoft">Không mở được trang này. Tải lại trang để thử lại.</p>
        <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
          <RotateCw aria-hidden /> Tải lại trang
        </Button>
      </div>
    )
  }
}

/** Đường cũ /comments, /comments/:id, /chat-channels → trang Gom Đơn /inbox, không để lại lịch sử (paths.ts legacyGomdonTarget) */
export function GomdonRedirect({ to }: { to: 'comments' | 'chat-channels' }) {
  const { id } = useParams()
  const { search } = useLocation()
  return <Navigate replace to={legacyGomdonTarget(to, id, search)} />
}

export function GomdonPage() {
  const access = useGomdonAccess()
  if (!access.enabled) return access.loading ? <Loading /> : <Unavailable access={access} />
  return (
    <PageErrorBoundary>
      <Suspense fallback={<Loading />}>
        <InboxPage />
      </Suspense>
    </PageErrorBoundary>
  )
}
